/**
 * Program lifecycle lint: anchor + mode resolution and ordering rules.
 *
 * The supported program structure is flat column-0 module code in lifecycle order —
 * hardware construction, then dashboard streaming finalization (the managed
 * `#region - Data streaming` block ending in `DataStreamStore.update_monitor_data()`, or its
 * legacy `DataStreamStore.get_instance()` form),
 * then `RobotBase().start()` as the final run step. The lint rules compare statement
 * positions against the two lifecycle anchors resolved here.
 *
 * These rules are deliberately heuristic. Documented limits (tracked as deferred
 * work in BPS-784; f-string replacement fields in BPS-782):
 * - Reachability is inline source order only: conditional truthiness is not evaluated
 *   (`if False: while True: ...` still flags) and module-level calls to user-defined
 *   functions that loop internally are not chased (no call-graph analysis).
 * - Loop bounds are not analyzed: `for i in range(10**9)` stays silent, and a
 *   `while True` whose `break` is unreachable in practice is only a warning.
 * - Blocking calls are a small curated denylist, not general long-running detection,
 *   and f-string replacement fields are blanked with the rest of the literal, so a
 *   call like `print(f"{input()}")` is not detected (BPS-782 tracks the follow-up).
 * - Receiver identity is last-write-wins per scope: control-flow paths are not joined
 *   (no bound/unbound/unknown lattice). Except-clause matching compares simple
 *   exception names only: bare except, Exception/BaseException, and identical names
 *   are presumed to catch; provably different names count as a possible exit.
 *   Subclass relationships are not modeled (a subclass catch under a different name
 *   degrades the loop to a warning — downward).
 * - Definition-time expressions are not scanned: `def helper(value=input()):` default
 *   arguments and other def-header expressions evaluate at definition time in Python
 *   but stay invisible to the blocking-call rule.
 * - Same-line ordering beyond direct forms is not modeled: a bound `robot.start()`
 *   sharing a line with later hardware, or a blocking call sharing the anchor line,
 *   is not position-compared.
 * - When uncertain, severity degrades downward (error -> warning -> silent), never up:
 *   false confidence in an error costs more trust than a missed warning.
 */

import { Diagnostic, DiagnosticSeverity } from 'vscode-languageserver-protocol';
import { FrameworkClasses } from '../../../framework/frameworkClasses';
import { FrameworkClassType } from '../../../framework/framework-types';
import { findRegionEndLineIndexAfterStart, isDataStreamingRegionStartLine } from '../../../utils/programRegionMarkers';
import {
  collectModuleFlowStatements,
  extractInlineSuiteText,
  leadingWhitespaceLength,
  PythonModuleFlowStatement,
  replacePythonStringContentsWithSpaces,
  splitCodeLines,
  VAR_ASSIGN_RE,
} from '../../../utils/pythonSourceScanUtils';

export interface LifecycleAnchorSpan {
  /** 0-based first line of the anchor construct. */
  startLineIndex: number;
  /** 0-based last line of the anchor construct (region `#endregion`, or the call line itself). */
  endLineIndex: number;
}

export type ProgramLifecycleMode = 'framework' | 'python-api';

export interface ProgramLifecycleAnchors {
  /**
   * Streaming-finalization anchor: the managed Data-streaming region when present at
   * top level, otherwise a top-level `DataStreamStore...update_monitor_data()` call.
   * Null when the program has no streaming anchor (streaming-relative rules skip).
   */
  streaming: LifecycleAnchorSpan | null;
  /** First top-level managed `RobotBase().start()` (or bound `robot.start()`) call. */
  start: LifecycleAnchorSpan | null;
  /** Framework mode when any managed RobotBase start call exists in the program. */
  mode: ProgramLifecycleMode;
  /**
   * True when a lifecycle anchor exists but only nested inside a function, class, or
   * conditional. The supported flat structure cannot be assumed, so lifecycle linting
   * is suppressed entirely rather than risking false positives.
   */
  suppressed: boolean;
}

/** Optional module qualifier(s) ahead of a framework name, e.g. `sphero.`. */
const QUALIFIER_PREFIX = /(?:[A-Za-z_]\w*\s*\.\s*)*/.source;
/** Direct streaming-finalization call: `[sphero.]DataStreamStore...update_monitor_data(`. */
const DIRECT_UPDATE_MONITOR_DATA_RE = new RegExp(`^${QUALIFIER_PREFIX}DataStreamStore\\b[\\w.\\s()]*\\.\\s*update_monitor_data\\s*\\(`);
/** Receiver-variable streaming call: `<name>.update_monitor_data(` — proven via bindings. */
const RECEIVER_UPDATE_MONITOR_DATA_RE = /^([A-Za-z_]\w*)\s*\.\s*update_monitor_data\s*\(/;
/** RHS that binds a variable to the DataStreamStore instance. */
const DATA_STREAM_STORE_INSTANCE_RHS_RE = new RegExp(`^${QUALIFIER_PREFIX}DataStreamStore\\s*\\.\\s*get_instance\\s*\\(`);
/** Direct managed start call: `[sphero.]RobotBase().start(`. */
const ROBOT_BASE_DIRECT_START_RE = new RegExp(`^${QUALIFIER_PREFIX}RobotBase\\s*\\(\\s*\\)\\s*\\.\\s*start\\s*\\(`);
/** Receiver-variable start call: `<name>.start(` — proven via bindings. */
const RECEIVER_START_RE = /^([A-Za-z_]\w*)\s*\.\s*start\s*\(/;
/** RHS that binds a variable to a RobotBase instance. */
const ROBOT_BASE_RHS_RE = new RegExp(`^${QUALIFIER_PREFIX}RobotBase\\s*\\(`);
/** Simple `name = <rhs>` assignment (optionally annotated), excluding comparisons. */
const SIMPLE_ASSIGNMENT_RE = /^([A-Za-z_]\w*)\s*(?::\s*[^=]+?)?=(?!=)\s*(.*)$/;

/**
 * Semicolon-separated sibling statements of a string-blanked line, trimmed.
 * Semicolons only separate statements in Python (they cannot appear inside
 * brackets outside strings, and strings are already blanked).
 */
function splitBlankedSemicolonSegments(blankedText: string): string[] {
  if (!blankedText.includes(';')) return [blankedText];
  return blankedText
    .split(';')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
}

/**
 * String-safe statement context for anchor resolution: all logical statements
 * (including those inside `def`/`class` suites) so lines inside triple-quoted
 * strings are never mistaken for anchors, and nested anchors are still seen.
 */
interface AnchorScanContext {
  statements: PythonModuleFlowStatement[];
  /** Lines that continue a multiline statement (e.g. inside a triple-quoted string). */
  multilineContinuationLines: ReadonlySet<number>;
}

function buildAnchorScanContext(code: string): AnchorScanContext {
  const statements = collectModuleFlowStatements(code, { includeDefSuites: true });
  // Precomputed line mask keeps region-marker containment checks O(1) per line;
  // scanning every span per candidate was quadratic on large pathological programs.
  const multilineContinuationLines = new Set<number>();
  for (const statement of statements) {
    for (let line = statement.startLineIndex + 1; line <= statement.endLineIndex; line++) {
      multilineContinuationLines.add(line);
    }
  }
  return { statements, multilineContinuationLines };
}

/** True when `lineIndex` is a continuation line of a multiline statement (e.g. inside a triple-quoted string). */
function isLineInsideMultilineStatement(context: AnchorScanContext, lineIndex: number): boolean {
  return context.multilineContinuationLines.has(lineIndex);
}

function isTopLevelStatement(statement: PythonModuleFlowStatement): boolean {
  return statement.indentColumn === 0 && !statement.insideDefOrClass;
}

/** All real Data-streaming region spans: marker lines that are not inside multiline statements. */
function findDataStreamingRegionCandidates(lines: string[], context: AnchorScanContext): { startLine: number; endLine: number }[] {
  const candidates: { startLine: number; endLine: number }[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (!isDataStreamingRegionStartLine(lines[i] ?? '')) continue;
    if (isLineInsideMultilineStatement(context, i)) continue;
    const endLine = findRegionEndLineIndexAfterStart(lines, i);
    if (endLine === null || isLineInsideMultilineStatement(context, endLine)) continue;
    candidates.push({ startLine: i, endLine });
    i = endLine;
  }
  return candidates;
}

/**
 * The finalization call statement inside a region span, if present. Region interiors
 * are BPS-managed, so both the direct DataStreamStore form and a bare receiver form
 * are trusted there without provenance tracking.
 */
function findFinalizationCallInRegion(context: AnchorScanContext, span: { startLine: number; endLine: number }): LifecycleAnchorSpan | null {
  for (const statement of context.statements) {
    if (statement.startLineIndex <= span.startLine || statement.startLineIndex >= span.endLine) continue;
    if (statement.kind !== 'other') continue;
    const text = replacePythonStringContentsWithSpaces(statement.headerText);
    if (DIRECT_UPDATE_MONITOR_DATA_RE.test(text) || RECEIVER_UPDATE_MONITOR_DATA_RE.test(text)) {
      return { startLineIndex: statement.startLineIndex, endLineIndex: statement.endLineIndex };
    }
  }
  return null;
}

function resolveStreamingAnchor(lines: string[], context: AnchorScanContext): { anchor: LifecycleAnchorSpan | null; nestedOnly: boolean } {
  // A top-level anchor always beats nested duplicates: scan every region candidate
  // (an earlier indented copy must not shadow the real top-level region), then fall
  // back to a top-level proven streaming call before concluding nested-only.
  const regionCandidates = findDataStreamingRegionCandidates(lines, context);
  const topLevelRegion = regionCandidates.find((span) => leadingWhitespaceLength(lines[span.startLine] ?? '') === 0);
  if (topLevelRegion) {
    // Streaming is finalized at the update_monitor_data() call, not at the region
    // markers: anchoring on the call keeps loops before it and hardware after it
    // (but still inside the region) visible to the ordering rules.
    const finalizationCall = findFinalizationCallInRegion(context, topLevelRegion);
    return {
      anchor: finalizationCall ?? { startLineIndex: topLevelRegion.startLine, endLineIndex: topLevelRegion.endLine },
      nestedOnly: false,
    };
  }

  const callScan = scanStreamingCallStatements(context, lines);
  if (callScan.anchor) return callScan;

  return { anchor: null, nestedOnly: regionCandidates.length > 0 || callScan.nestedOnly };
}

/**
 * Receiver-binding tracker with lexical frames. Module identity is updated by
 * module-executed assignments at any indent (conditional rebinds included);
 * `def` and `class` suites get local frames so local bindings and shadows
 * resolve within their owner without creating, replacing, or deleting module
 * receiver identity. Lookups resolve innermost frame first, then module scope.
 */
function createLexicalBindingTracker(boundRhsRe: RegExp) {
  const moduleBindings = new Set<string>();
  const frames: { ownerIndentColumn: number; ownerKind: 'def' | 'class'; bindings: Map<string, boolean> }[] = [];

  return {
    enterStatement(statement: PythonModuleFlowStatement) {
      while (frames.length > 0 && (frames[frames.length - 1]?.ownerIndentColumn ?? 0) >= statement.indentColumn) {
        frames.pop();
      }
      if (statement.kind === 'def' || statement.kind === 'class') {
        frames.push({ ownerIndentColumn: statement.indentColumn, ownerKind: statement.kind, bindings: new Map() });
      }
    },
    recordAssignment(name: string, rhs: string) {
      const bound = boundRhsRe.test(rhs);
      const frame = frames[frames.length - 1];
      if (frame) {
        frame.bindings.set(name, bound);
      } else if (bound) {
        moduleBindings.add(name);
      } else {
        moduleBindings.delete(name);
      }
    },
    isBound(name: string): boolean {
      for (let i = frames.length - 1; i >= 0; i--) {
        const frame = frames[i];
        if (!frame) continue;
        // Python bare-name resolution never reads class scopes from nested
        // functions (or nested classes): only the innermost frame may be a class.
        if (frame.ownerKind === 'class' && i !== frames.length - 1) continue;
        const value = frame.bindings.get(name);
        if (value !== undefined) return value;
      }
      return moduleBindings.has(name);
    },
  };
}

/**
 * Sequential scan for the streaming-finalization call with receiver provenance:
 * only direct `DataStreamStore...` calls or receivers proven bound to
 * `DataStreamStore.get_instance()` in the resolving scope count as anchor or
 * suppression signals. An unrelated `cache.update_monitor_data()` neither
 * anchors nor suppresses.
 */
function scanStreamingCallStatements(context: AnchorScanContext, lines: string[]): { anchor: LifecycleAnchorSpan | null; nestedOnly: boolean } {
  const tracker = createLexicalBindingTracker(DATA_STREAM_STORE_INSTANCE_RHS_RE);
  let anchor: LifecycleAnchorSpan | null = null;
  let sawNestedCall = false;

  for (const statement of context.statements) {
    tracker.enterStatement(statement);
    const text = replacePythonStringContentsWithSpaces(statement.headerText);

    // Compound statements cannot be the streaming call themselves (a
    // `def update_monitor_data():` header must not anchor), but their inline
    // suites can contain one: `if ready: store.update_monitor_data()` is a
    // nested-only signal. Extraction reads the full logical text so inline
    // suites on multiline headers (`if (\n    ready\n): ...`) are seen too.
    if (statement.kind !== 'other') {
      const inlineSuiteText = extractInlineSuiteText(blankedStatementText(statement, lines));
      if (inlineSuiteText !== null) {
        for (const segment of splitBlankedSemicolonSegments(inlineSuiteText)) {
          const receiverName = segment.match(RECEIVER_UPDATE_MONITOR_DATA_RE)?.[1];
          if (DIRECT_UPDATE_MONITOR_DATA_RE.test(segment) || (receiverName && tracker.isBound(receiverName))) {
            sawNestedCall = true;
          }
        }
      }
      continue;
    }

    for (const segment of splitBlankedSemicolonSegments(text)) {
      const isDirectCall = DIRECT_UPDATE_MONITOR_DATA_RE.test(segment);
      const receiverName = isDirectCall ? null : segment.match(RECEIVER_UPDATE_MONITOR_DATA_RE)?.[1];
      if (isDirectCall || (receiverName && tracker.isBound(receiverName))) {
        if (anchor === null && isTopLevelStatement(statement)) {
          anchor = { startLineIndex: statement.startLineIndex, endLineIndex: statement.endLineIndex };
        } else if (!isTopLevelStatement(statement)) {
          sawNestedCall = true;
        }
        continue;
      }

      const assignMatch = segment.match(SIMPLE_ASSIGNMENT_RE);
      if (assignMatch?.[1]) tracker.recordAssignment(assignMatch[1], assignMatch[2] ?? '');
    }
  }

  return { anchor, nestedOnly: anchor === null && sawNestedCall };
}

/**
 * Sequential scan for managed RobotBase start calls with lexically scoped receiver
 * identity: `RobotBase().start()` directly, or `<name>.start()` where `<name>` is
 * bound to `RobotBase(...)` in the resolving scope. Module identity is updated by
 * module-executed assignments (including conditional rebinds in module-level
 * suites); function-local bindings and shadows resolve inside their own def and
 * never touch the module binding. Statement-based scanning keeps docstring text
 * out of the receiver set.
 */
function resolveStartAnchor(
  context: AnchorScanContext,
  lines: string[]
): { anchor: LifecycleAnchorSpan | null; nestedOnly: boolean; managed: boolean } {
  const tracker = createLexicalBindingTracker(ROBOT_BASE_RHS_RE);
  let anchor: LifecycleAnchorSpan | null = null;
  let sawNestedCall = false;
  let managed = false;

  for (const statement of context.statements) {
    tracker.enterStatement(statement);
    const text = replacePythonStringContentsWithSpaces(statement.headerText);

    // Inline suites of compound statements can hide a managed start:
    // `if ready: RobotBase().start()` is a nested-only signal, never an anchor.
    // Extraction reads the full logical text so multiline headers are seen too.
    if (statement.kind !== 'other') {
      const inlineSuiteText = extractInlineSuiteText(blankedStatementText(statement, lines));
      if (inlineSuiteText !== null) {
        for (const segment of splitBlankedSemicolonSegments(inlineSuiteText)) {
          const receiverName = segment.match(RECEIVER_START_RE)?.[1];
          if (ROBOT_BASE_DIRECT_START_RE.test(segment) || (receiverName && tracker.isBound(receiverName))) {
            managed = true;
            sawNestedCall = true;
          }
        }
      }
      continue;
    }

    for (const segment of splitBlankedSemicolonSegments(text)) {
      const isDirectStart = ROBOT_BASE_DIRECT_START_RE.test(segment);
      const receiverName = isDirectStart ? null : segment.match(RECEIVER_START_RE)?.[1];
      if (isDirectStart || (receiverName && tracker.isBound(receiverName))) {
        managed = true;
        if (anchor === null && isTopLevelStatement(statement)) {
          anchor = { startLineIndex: statement.startLineIndex, endLineIndex: statement.endLineIndex };
        } else if (!isTopLevelStatement(statement)) {
          sawNestedCall = true;
        }
        continue;
      }

      const assignMatch = segment.match(SIMPLE_ASSIGNMENT_RE);
      if (assignMatch?.[1]) tracker.recordAssignment(assignMatch[1], assignMatch[2] ?? '');
    }
  }

  return { anchor, nestedOnly: anchor === null && sawNestedCall, managed };
}

/**
 * Resolves the lifecycle anchors and program mode used by the lifecycle lint rules.
 *
 * Anchor semantics follow the accepted BPS-738 decisions:
 * - No streaming anchor: streaming-relative rules skip; start-relative rules still run.
 * - Anchor present but only nested (moved into a function/conditional): `suppressed`
 *   is true and all lifecycle linting is disabled for the program.
 * - A top-level anchor plus stray nested duplicates keeps the top-level anchor
 *   (the supported structure exists, so linting stays active).
 */
export function resolveProgramLifecycleAnchors(code: string): ProgramLifecycleAnchors {
  const lines = splitCodeLines(code);
  const context = buildAnchorScanContext(code);

  const streaming = resolveStreamingAnchor(lines, context);
  const start = resolveStartAnchor(context, lines);

  return {
    streaming: streaming.anchor,
    start: start.anchor,
    mode: start.managed ? 'framework' : 'python-api',
    suppressed: streaming.nestedOnly || start.nestedOnly,
  };
}

// ---------------------------------------------------------------------------
// Rule 1: loops and blocking calls that prevent lifecycle setup from running
// ---------------------------------------------------------------------------

export const LIFECYCLE_DIAGNOSTIC_SOURCE = 'bps';
export const LIFECYCLE_LOOP_BEFORE_START_CODE = 'lifecycle-loop-before-start';
export const LIFECYCLE_LOOP_BEFORE_STREAMING_CODE = 'lifecycle-loop-before-streaming';
export const LIFECYCLE_BLOCKING_CALL_CODE = 'lifecycle-blocking-call';
export const LIFECYCLE_HARDWARE_AFTER_STREAMING_CODE = 'lifecycle-hardware-after-streaming';
export const LIFECYCLE_HARDWARE_AFTER_START_CODE = 'lifecycle-hardware-after-start';
export const LIFECYCLE_STREAMING_AFTER_START_CODE = 'lifecycle-streaming-after-start';

export const LIFECYCLE_LINT_MESSAGE_DEFAULTS = {
  loopBlocksStreamingAndStart:
    'This loop never finishes, so the data streaming setup and RobotBase().start() below it will never run. Create hardware first, configure streaming, then start your robot — keep long-running loops after the start call or inside actions.',
  loopBlocksStart:
    'This loop never finishes, so RobotBase().start() below it will never run. Keep long-running loops after the start call or inside actions.',
  loopMayDelayStreamingAndStart:
    'The data streaming setup and RobotBase().start() below this loop will not run until it finishes. Make sure this loop completes quickly.',
  loopMayDelayStart: 'RobotBase().start() below this loop will not run until it finishes. Make sure this loop completes quickly.',
  loopBlocksStreaming:
    'This loop never finishes, so the data streaming setup below it will never run. Configure data streaming before starting your program loop.',
  loopMayDelayStreaming: 'The data streaming setup below this loop will not run until it finishes. Make sure this loop completes quickly.',
  blockingCallBeforeStreamingAndStart:
    'This call can block your program before data streaming and RobotBase().start() are set up. Keep blocking calls after setup finishes or inside actions.',
  blockingCallBeforeStart:
    'This call can block your program before RobotBase().start() runs. Keep blocking calls after the start call or inside actions.',
  blockingCallBeforeStreaming:
    'This call can block your program before data streaming is configured. Set up data streaming before long-running calls.',
  hardwareAfterStreaming:
    'This hardware is created after data streaming is configured, so it will not be included in dashboard streaming. Create all hardware before the data streaming setup.',
  hardwareAfterStart:
    'This hardware is created after RobotBase().start(), so this line will never run. Create all hardware first, configure data streaming second, and keep RobotBase().start() as the last line.',
  streamingAfterStart:
    'This data streaming setup is after RobotBase().start(), so it will never run and the dashboard will not receive data. Move the data streaming setup above RobotBase().start(), and keep the start call as the last line.',
} as const;

export type LifecycleLintMessages = { [K in keyof typeof LIFECYCLE_LINT_MESSAGE_DEFAULTS]: string };

/**
 * Known blocking calls flagged ahead of lifecycle setup. Deliberately small and
 * curated: general long-running-call detection is undecidable, so only calls that
 * are unambiguously blocking belong here.
 */
const BLOCKING_CALL_RE = /(^|[^\w.])(time\s*\.\s*sleep|input)\s*\(/;

const LIFECYCLE_DIAGNOSTIC_CODES: ReadonlySet<string> = new Set([
  LIFECYCLE_LOOP_BEFORE_START_CODE,
  LIFECYCLE_LOOP_BEFORE_STREAMING_CODE,
  LIFECYCLE_BLOCKING_CALL_CODE,
  LIFECYCLE_HARDWARE_AFTER_STREAMING_CODE,
  LIFECYCLE_HARDWARE_AFTER_START_CODE,
  LIFECYCLE_STREAMING_AFTER_START_CODE,
]);

/** Mechanism and electronic framework classes — the hardware the lifecycle rules order. */
const HARDWARE_CLASS_NAMES: ReadonlySet<string> = new Set(
  FrameworkClasses.filter(
    (frameworkClass) => frameworkClass.type === FrameworkClassType.Mechanism || frameworkClass.type === FrameworkClassType.Electronic
  ).map((frameworkClass) => frameworkClass.name)
);

function statementRange(statement: PythonModuleFlowStatement, lines: string[]) {
  const line = lines[statement.startLineIndex] ?? '';
  return {
    start: { line: statement.startLineIndex, character: leadingWhitespaceLength(line) },
    end: { line: statement.startLineIndex, character: line.length },
  };
}

function whileLoopHasExit(statement: PythonModuleFlowStatement): boolean {
  return Boolean(statement.loopExits?.hasBreak || statement.loopExits?.hasReturn || statement.loopExits?.hasRaise);
}

/** `while True:` / `while 1:` headers, whose conditions can never become false. */
const ALWAYS_TRUE_WHILE_RE = /^while\s+(True|1)\s*:/;

function isProvablyNonTerminatingLoop(statement: PythonModuleFlowStatement): boolean {
  return ALWAYS_TRUE_WHILE_RE.test(statement.headerText) && !whileLoopHasExit(statement);
}

interface LoopMessagePick {
  message: string;
  severity: DiagnosticSeverity;
  code: string;
}

function pickFrameworkLoopDiagnostic(
  statement: PythonModuleFlowStatement,
  streaming: LifecycleAnchorSpan | null,
  messages: LifecycleLintMessages
): LoopMessagePick {
  const beforeStreaming = streaming !== null && statement.endLineIndex < streaming.startLineIndex;
  if (isProvablyNonTerminatingLoop(statement)) {
    return {
      message: beforeStreaming ? messages.loopBlocksStreamingAndStart : messages.loopBlocksStart,
      severity: DiagnosticSeverity.Error,
      code: LIFECYCLE_LOOP_BEFORE_START_CODE,
    };
  }
  return {
    message: beforeStreaming ? messages.loopMayDelayStreamingAndStart : messages.loopMayDelayStart,
    severity: DiagnosticSeverity.Warning,
    code: LIFECYCLE_LOOP_BEFORE_START_CODE,
  };
}

function pickPythonApiLoopDiagnostic(statement: PythonModuleFlowStatement, messages: LifecycleLintMessages): LoopMessagePick {
  // Python-API programs legitimately end in a main loop, so severity is capped at
  // warning even for provably non-terminating loops ahead of the streaming setup.
  return {
    message: isProvablyNonTerminatingLoop(statement) ? messages.loopBlocksStreaming : messages.loopMayDelayStreaming,
    severity: DiagnosticSeverity.Warning,
    code: LIFECYCLE_LOOP_BEFORE_STREAMING_CODE,
  };
}

/**
 * Full logical-statement text with string contents blanked and `#` comments
 * stripped, so scans see continuation lines without matching strings or comments.
 */
function blankedStatementText(statement: PythonModuleFlowStatement, lines: string[]): string {
  if (statement.endLineIndex === statement.startLineIndex) {
    return replacePythonStringContentsWithSpaces(statement.headerText);
  }
  const fullText = lines.slice(statement.startLineIndex, statement.endLineIndex + 1).join('\n');
  return replacePythonStringContentsWithSpaces(fullText).replace(/#[^\n]*/g, '');
}

function lineRange(lineIndex: number, lines: string[]) {
  const line = lines[lineIndex] ?? '';
  return {
    start: { line: lineIndex, character: leadingWhitespaceLength(line) },
    end: { line: lineIndex, character: line.length },
  };
}

function buildLoopAndBlockingCallDiagnostics(
  code: string,
  anchors: ProgramLifecycleAnchors,
  lines: string[],
  messages: LifecycleLintMessages
): Diagnostic[] {
  const isFramework = anchors.mode === 'framework';
  const boundaryLineIndex = isFramework ? anchors.start?.startLineIndex ?? null : anchors.streaming?.startLineIndex ?? null;
  if (boundaryLineIndex === null) return [];

  const diagnostics: Diagnostic[] = [];

  for (const statement of collectModuleFlowStatements(code)) {
    if (statement.startLineIndex >= boundaryLineIndex) break;

    if (statement.kind === 'while') {
      const pick = isFramework
        ? pickFrameworkLoopDiagnostic(statement, anchors.streaming, messages)
        : pickPythonApiLoopDiagnostic(statement, messages);
      diagnostics.push({
        message: pick.message,
        severity: pick.severity,
        source: LIFECYCLE_DIAGNOSTIC_SOURCE,
        code: pick.code,
        range: statementRange(statement, lines),
      });
      continue;
    }

    // Statements can carry blocking calls anywhere in their logical text: inline
    // suites like `if slow: time.sleep(5)` and continuation lines of multiline
    // calls like `consume(\n    time.sleep(5),\n)`. `def`/`class` headers are
    // excluded: a one-line def body only runs when called. (`while` headers
    // already emitted a loop diagnostic and continued above.)
    if (statement.kind !== 'def' && statement.kind !== 'class' && BLOCKING_CALL_RE.test(blankedStatementText(statement, lines))) {
      // Message matches the lifecycle context: python-api programs are told about
      // streaming only (they have no RobotBase start), and framework programs are
      // told what the call actually delays at its position.
      const beforeStreaming = anchors.streaming !== null && statement.endLineIndex < anchors.streaming.startLineIndex;
      const message = !isFramework
        ? messages.blockingCallBeforeStreaming
        : beforeStreaming
        ? messages.blockingCallBeforeStreamingAndStart
        : messages.blockingCallBeforeStart;
      diagnostics.push({
        message,
        severity: DiagnosticSeverity.Warning,
        source: LIFECYCLE_DIAGNOSTIC_SOURCE,
        code: LIFECYCLE_BLOCKING_CALL_CODE,
        range: statementRange(statement, lines),
      });
    }
  }

  return diagnostics;
}

function buildHardwareOrderDiagnostics(
  code: string,
  anchors: ProgramLifecycleAnchors,
  lines: string[],
  messages: LifecycleLintMessages
): Diagnostic[] {
  const streamingEnd = anchors.streaming?.endLineIndex ?? null;
  const startLine = anchors.start?.startLineIndex ?? null;
  if (streamingEnd === null && startLine === null) return [];

  const diagnostics: Diagnostic[] = [];

  // Walker-based scan (not a raw line scan) so hardware-looking text inside
  // triple-quoted strings is never flagged. Any module-executed construction
  // counts, including conditional suites (per the widened AC): only def bodies
  // (deferred) and class bodies (class attributes) are excluded. Semicolon
  // siblings are scanned per segment; on a line shared with the start or
  // streaming call, segment position decides before/after (direct call forms
  // only — a bound-receiver call sharing a line with hardware is not modeled).
  for (const statement of collectModuleFlowStatements(code)) {
    if (statement.insideDefOrClass) continue;
    let blanked: string;
    if (statement.kind === 'other') {
      blanked = replacePythonStringContentsWithSpaces(statement.headerText);
    } else if (statement.kind !== 'def' && statement.kind !== 'class') {
      // Inline suites of executable compounds (`if extra: arm = Motor(...)`) carry
      // hardware too; def/class headers stay excluded (deferred / class attributes).
      const inlineSuiteText = extractInlineSuiteText(blankedStatementText(statement, lines));
      if (inlineSuiteText === null) continue;
      blanked = inlineSuiteText;
    } else {
      continue;
    }
    const sameLineStartCallOffset = startLine === statement.startLineIndex ? blanked.search(/RobotBase\s*\(\s*\)\s*\.\s*start\s*\(/) : -1;
    const sameLineStreamingCallOffset = streamingEnd === statement.startLineIndex ? blanked.search(/\bupdate_monitor_data\s*\(/) : -1;

    let segmentOffset = 0;
    for (const rawSegment of blanked.split(';')) {
      const currentOffset = segmentOffset;
      segmentOffset += rawSegment.length + 1;
      const assignMatch = rawSegment.trim().match(VAR_ASSIGN_RE);
      const className = assignMatch?.[2] ?? '';
      if (!assignMatch || !HARDWARE_CLASS_NAMES.has(className)) continue;

      // After start is strictly worse than after streaming: emit only the start rule.
      const afterStart =
        startLine !== null && (statement.startLineIndex > startLine || (sameLineStartCallOffset >= 0 && currentOffset > sameLineStartCallOffset));
      if (afterStart) {
        diagnostics.push({
          message: messages.hardwareAfterStart,
          severity: DiagnosticSeverity.Error,
          source: LIFECYCLE_DIAGNOSTIC_SOURCE,
          code: LIFECYCLE_HARDWARE_AFTER_START_CODE,
          range: lineRange(statement.startLineIndex, lines),
        });
        continue;
      }
      const afterStreaming =
        streamingEnd !== null &&
        (statement.startLineIndex > streamingEnd || (sameLineStreamingCallOffset >= 0 && currentOffset > sameLineStreamingCallOffset));
      if (afterStreaming) {
        diagnostics.push({
          message: messages.hardwareAfterStreaming,
          severity: DiagnosticSeverity.Warning,
          source: LIFECYCLE_DIAGNOSTIC_SOURCE,
          code: LIFECYCLE_HARDWARE_AFTER_STREAMING_CODE,
          range: lineRange(statement.startLineIndex, lines),
        });
      }
    }
  }

  return diagnostics;
}

/**
 * Streaming finalization positioned after the start call.
 *
 * `RobotBase().start()` runs a blocking loop for the life of the program, so a streaming
 * anchor below it never executes and the dashboard silently receives nothing. The resolved
 * anchor is the top-level `update_monitor_data()` finalization call — the call itself even when
 * it sits inside the managed Data-streaming region — so a moved region and a moved lone
 * registration are both covered, and the diagnostic lands on the statement that never runs.
 *
 * This rule only reports; nothing here rewrites the program. Dashboard synchronization
 * deliberately keeps generating into a misplaced region, and moving it back is the user's call.
 */
function buildStreamingOrderDiagnostics(anchors: ProgramLifecycleAnchors, lines: string[], messages: LifecycleLintMessages): Diagnostic[] {
  const streamingLine = anchors.streaming?.startLineIndex ?? null;
  const startLine = anchors.start?.startLineIndex ?? null;
  if (streamingLine === null || startLine === null || streamingLine <= startLine) return [];

  return [
    {
      message: messages.streamingAfterStart,
      severity: DiagnosticSeverity.Error,
      source: LIFECYCLE_DIAGNOSTIC_SOURCE,
      code: LIFECYCLE_STREAMING_AFTER_START_CODE,
      range: lineRange(streamingLine, lines),
    },
  ];
}

/**
 * Builds the lifecycle lint diagnostics:
 * - Rule 1: `while` loops (at any indent reachable in module-execution flow) and
 *   known blocking calls ahead of the lifecycle setup anchors. `for` loops are
 *   presumed bounded initialization and stay silent; their bodies are still walked,
 *   so nested `while` loops and blocking calls inside them are flagged on their own.
 * - Rule 2: hardware constructed after streaming finalization (won't be streamed).
 * - Rule 3: hardware constructed after `RobotBase().start()` (never runs — error).
 * - Rule 4: streaming finalization after `RobotBase().start()` (never runs — error).
 */
export function buildLifecycleDiagnostics(code: string, messages: LifecycleLintMessages): Diagnostic[] {
  if (!code) return [];

  const anchors = resolveProgramLifecycleAnchors(code);
  if (anchors.suppressed) return [];

  const lines = splitCodeLines(code);
  return [
    ...buildLoopAndBlockingCallDiagnostics(code, anchors, lines, messages),
    ...buildHardwareOrderDiagnostics(code, anchors, lines, messages),
    ...buildStreamingOrderDiagnostics(anchors, lines, messages),
  ];
}

/** Replaces any previous lifecycle diagnostics with freshly computed ones. */
export function mergeLifecycleDiagnostics(code: string, diagnostics: Diagnostic[], messages: LifecycleLintMessages): Diagnostic[] {
  const withoutPreviousLifecycleDiagnostics = diagnostics.filter(
    (diagnostic) => diagnostic.source !== LIFECYCLE_DIAGNOSTIC_SOURCE || !LIFECYCLE_DIAGNOSTIC_CODES.has(String(diagnostic.code ?? ''))
  );

  return [...withoutPreviousLifecycleDiagnostics, ...buildLifecycleDiagnostics(code, messages)];
}
